import { useSelector } from "react-redux";

import { RootState } from "src/redux/reducers";
import { IProject } from "src/redux/project/types";

type UseGetProjectAutocompleteResult = IProject | null;

/**
 * Custom hook to retrieve a specific project from the autocomplete list.
 *
 * This hook uses selective Redux selection to prevent unnecessary re-renders
 * when unrelated projectReducer fields are updated via websockets.
 *
 * It searches through the workspace project autocomplete list and returns
 * the matching project by projectId, ensuring the component only re-renders
 * when the specific project data actually changes.
 *
 * @param {string | null} projectId - The UUID of the project to retrieve, or null
 * @returns {UseGetProjectAutocompleteResult} The project object matching the projectId, or null if not found or projectId is null
 *
 * @example
 * ```tsx
 * const project = useGetProjectAutocomplete(projectId);
 *
 * useEffect(() => {
 *   if (project) {
 *     console.log(`Project name: ${project.projectName}`);
 *   }
 * }, [project]);
 * ```
 */
export default function useGetProjectAutocomplete(
  projectId: string | null,
): UseGetProjectAutocompleteResult {
  return useSelector((state: RootState): UseGetProjectAutocompleteResult => {
    const projectAutocomplete = state.projectReducer.wsProjectAutocompleteList;
    return (
      projectAutocomplete.find((project) => project.projectId === projectId) ??
      null
    );
  });
}
